/**
 * The rules the recovery screens run on, on both sides of the feature: naming the contacts who will
 * hold pieces of a key, and the episode where those pieces are called back in.
 *
 * A user's recovery key is split into five pieces and any three of them rebuild it, which is where
 * every number below comes from: five contacts is the ceiling, three is what makes recovery work at
 * all, and the single deletion the API refuses is the one taking a working set of exactly three down
 * to two. The auth service is the authority on all of it — `MAXIMUM_KEY_SHARES` and
 * `KEY_SHARES_THRESHOLD` in `crypt::constants` — so these predicates exist to keep the interface
 * from offering an action that would only come back as a 400 or a 422.
 *
 * Two counts run through here and they are not interchangeable. `acceptedContacts` is what the API
 * measures when it decides whether a delete may go through. `readyContacts` is the one that means
 * something to a user: a contact's piece is sealed to them by a background job, and when that job
 * could not run the invite comes back from `recovery-key-shares/pending` — accepted, but unable to
 * help until the user finishes the handover themselves.
 *
 * The second half of the file is the recovery itself. A password reset that strands a user's key
 * opens one `account_recoveries` record, and its status is the whole state machine — which screen
 * renders, and which single action is allowed from it. The API refuses anything else, so the
 * predicates here exist to keep a button off the page rather than to explain a 422 afterwards.
 */
import {formatDate} from './data'
import {isEmpty, toSentence} from './tools'

export const MAXIMUM_CONTACTS = 5
export const REQUIRED_CONTACTS = 3

export const ACCOUNT = 'account'
export const RECOVERY = 'recovery'

export const DRAFT = 'draft'
export const SENT = 'sent'
export const ACCEPTED = 'accepted'
export const DECLINED = 'declined'

const STATUS_CHIPS = {
  [DRAFT]: {label: 'Draft', color: 'default'},
  [SENT]: {label: 'Invited', color: 'warning'},
  [ACCEPTED]: {label: 'Ready', color: 'success'},
  [DECLINED]: {label: 'Declined', color: 'error'}
}

const AWAITING_HANDOVER_CHIP = {label: 'Almost ready', color: 'warning'}

const IDLE_REASONS = {
  [DRAFT]: 'was never invited',
  [SENT]: "hasn't replied yet",
  [DECLINED]: 'declined'
}

export const acceptedContacts = (invites = []) => invites.filter(invite => invite.status === ACCEPTED)

export const idleContacts = (invites = []) => invites.filter(invite => invite.status !== ACCEPTED)

export const isAwaitingHandover = ({invite, pendingWraps = []}) => pendingWraps.some(pending => pending.id === invite.id)

export const readyContacts = ({invites = [], pendingWraps = []}) => (
  acceptedContacts(invites).filter(invite => !isAwaitingHandover({invite, pendingWraps}))
)

export const canAddContact = (invites = []) => invites.length < MAXIMUM_CONTACTS

export const isRemovalRefused = ({invite, invites = []}) => (
  invite.status === ACCEPTED && acceptedContacts(invites).length === REQUIRED_CONTACTS
)

export const contactChip = ({invite, pendingWraps}) => (
  isAwaitingHandover({invite, pendingWraps}) ? AWAITING_HANDOVER_CHIP : STATUS_CHIPS[invite.status]
)

export const contactDetail = ({invite, pendingWraps}) => {
  if (isAwaitingHandover({invite, pendingWraps}))
    return "Accepted, but their piece hasn't been handed over"

  switch (invite.status) {
    case ACCEPTED:
      return `Ready since ${formatDate(invite.acceptedAt)}`
    case SENT:
      return `Invited ${formatDate(invite.sentAt)} · waiting for them to accept`
    case DECLINED:
      return 'Declined · remove them to free up a slot'
    default:
      return 'Not invited yet'
  }
}

/**
 * Sending and resending are separate routes because the API treats them as separate acts: a draft
 * has never spent a share and a sent invite has to release the previous one first. An invite in any
 * other state has no send left in it, and offering one would earn a 422.
 */
export const contactAction = (invite = {}) => {
  switch (invite.status) {
    case DRAFT:
      return {label: 'Send invitation', path: 'send'}
    case SENT:
      return {label: 'Send again', path: 'resend'}
    default:
      return null
  }
}

const survivorsMessage = remaining => {
  if (remaining === 0)
    return 'No one will be left who can help you back in.'

  if (remaining < REQUIRED_CONTACTS)
    return `You'll be left with ${remaining} ${remaining === 1 ? 'person' : 'people'}, which still isn't enough to recover your account.`

  if (remaining === REQUIRED_CONTACTS)
    return `You'll be left with ${remaining} people who can help you back in — the fewest that works.`

  return `You'll be left with ${remaining} people who can help you back in.`
}

/**
 * What removing this contact actually costs. A contact who never accepted hands their piece back to
 * the pool, so the prompt is a courtesy. One who accepted takes their piece with it, and no amount
 * of inviting replacements brings it back — that is the part a user could not work out for
 * themselves, so it is said outright rather than implied.
 */
export const removalPrompt = ({invite, invites}) => {
  if (invite.status !== ACCEPTED)
    return {body: `${invite.firstName} hasn't accepted, so nothing is lost. You can invite someone else in their place.`}

  return {
    body: `${invite.firstName} holds one of the pieces of your key, and removing them destroys it for good. ${survivorsMessage(acceptedContacts(invites).length - 1)}`,
    warning: "You can't get this piece back by inviting someone else. It only returns if you go through a recovery."
  }
}

export const removalRefusedMessage = (invites = []) => {
  const names = toSentence(acceptedContacts(invites).map(invite => invite.firstName))

  return `${names} are exactly the three it takes, so removing one now would leave you unable to recover at all. Invite someone else and wait for them to accept first.`
}

export const contactLimitMessage = (invites = []) => {
  const idle = idleContacts(invites)

  if (isEmpty(idle))
    return "Five is the most you can have. To swap someone out, remove them first — but you'll lose the piece they hold."

  const reasons = toSentence(idle.map(invite => `${invite.firstName} ${IDLE_REASONS[invite.status]}`))

  return `${reasons}. Remove ${idle.length > 1 ? 'any one of them' : 'them'} to make room for someone else.`
}

export const recoveryStrength = ({invites, pendingWraps}) => {
  const ready = readyContacts({invites, pendingWraps}).length
  const outstanding = REQUIRED_CONTACTS - ready

  if (outstanding <= 0)
    return {ready, headline: 'Recovery is ready', detail: 'Any three of your contacts can let you back in.'}

  return {
    ready,
    headline: `${ready} of ${REQUIRED_CONTACTS} pieces ready`,
    detail: `${outstanding} more ${outstanding === 1 ? 'person needs' : 'people need'} to accept before recovery works.`
  }
}

export const recoverySummary = ({invites = [], pendingWraps = []}) => {
  if (isEmpty(invites))
    return 'Not set up. If you lose your password, no one can let you back in.'

  const ready = readyContacts({invites, pendingWraps}).length

  if (ready >= REQUIRED_CONTACTS)
    return `${ready} contacts ready to help you back in`

  return `${ready} of ${REQUIRED_CONTACTS} pieces ready. Finish setting up so someone can let you back in.`
}

export const PENDING = 'pending'
export const PREPARED = 'prepared'
export const REQUESTED = 'requested'
export const READY = 'ready'
export const FAILED = 'failed'

const UNAUTHORIZED = 401
const CONFLICT = 409
const SERVICE_UNAVAILABLE = 503

export const isWrongPassword = (response = {}) => response.statusCode === UNAUTHORIZED

/**
 * The recovery's key pair is still being generated, so there is nowhere yet for a released piece to
 * go. The API says as much with a 503 because the request was well formed and asking again shortly
 * is expected to work — which is the whole reason the screens read the status code rather than the
 * message.
 */
export const isKeyPairUnavailable = (response = {}) => response.statusCode === SERVICE_UNAVAILABLE

export const isRecoveryInProgress = (response = {}) => response.statusCode === CONFLICT

// Generation is normally seconds. Two minutes is long enough to be sure it is not coming, and short
// enough that nobody is left watching a bar that will never move.
export const KEY_PAIR_POLL_INTERVAL = 4000
export const KEY_PAIR_POLL_ATTEMPTS = 30

// A read that finds nothing answers with a generic 422, so an absent status is the only signal that
// no recovery is open. A genuine outage is indistinguishable, and lands the user on their contacts.
export const isRecoveryOpen = (recovery = {}) => Boolean(recovery.status)

export const awaitsKeyPair = (recovery = {}) => recovery.status === PENDING

export const needsKeyPairRetry = (recovery = {}) => recovery.status === FAILED

export const canAskContacts = (recovery = {}) => recovery.status === PREPARED || recovery.status === REQUESTED

export const canCompleteRecovery = (recovery = {}) => recovery.status === READY

const RECOVERY_DETAILS = {
  [PENDING]: "We're preparing your recovery. This usually takes a few seconds.",
  [FAILED]: 'Something went wrong preparing your recovery, and it needs starting again.',
  [PREPARED]: "Your contacts haven't been asked yet. Any three of them can let you back in.",
  [REQUESTED]: 'Your contacts have been asked. You can finish as soon as three of them reply.',
  [READY]: 'Your contacts came through. Enter your password to finish.'
}

export const recoveryNotice = (recovery = {}) => RECOVERY_DETAILS[recovery.status]
